import {
  Component,
  EventEmitter,
  Input,
  model,
  OnInit,
  Pipe,
  PipeTransform,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import currency from 'currency.js';
import {
  UploaderOptions,
  UploadFile,
  UploadInput,
  UploadOutput,
} from 'ngx-uploader-esb';
import { NgxUploaderModule } from 'ngx-uploader-esb';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import { IDealList } from '../../modules/advance-application/models/deal.interface';
import { IPromisedPayment } from '../../modules/advance-application/models/promised-payment.interface';
import { DealService } from '../../modules/advance-application/services/deal.service';
import { PromisedPaymentService } from '../../modules/advance-application/services/promised-payment.service';
import { CurrencyJsPipe } from '../../modules/pipes/currency-js.pipe';
import { DatePipe } from '../../modules/pipes/date.pipe';
import { LocalisePipe } from '../../modules/pipes/localise.pipe';
import { FormatDateEnum } from '../../modules/shared/models/format-date.enum';
import { UserDealDocumentDocumentTypeEnum } from '../../modules/shared/models/user-deal-document-document-type.enum';
import { DateService } from '../../modules/shared/services/date.service';
import { SharedService } from '../../modules/shared/services/shared.service';
import { IsMoreDaysPipe } from './is-more-days.pipe';

@Pipe({
  name: 'totalByPromisedList',
  standalone: true,
})
export class TotalByPromisedListPipe implements PipeTransform {
  transform(value?: IPromisedPayment[]): currency | undefined {
    if (!value) {
      return;
    }

    let res = currency(0);
    value.forEach((v) => (res = res.add(v.total)));

    return res;
  }
}

@Component({
  selector: 'app-change-closing-date',
  templateUrl: './change-closing-date.component.html',
  styleUrls: ['./change-closing-date.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatDividerModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    NgxUploaderModule,
    IsMoreDaysPipe,
    TotalByPromisedListPipe,
    DatePipe,
    CurrencyJsPipe,
    LocalisePipe,
  ],
})
export class ChangeClosingDateComponent implements OnInit {
  @Input() deal: IDealList;

  public promisedPayment?: IPromisedPayment;
  public formatDateEnum = FormatDateEnum;
  public promisedPaymentPreview?: IPromisedPayment[];
  public userDealDocumentDocumentTypeEnum = UserDealDocumentDocumentTypeEnum;
  public options: UploaderOptions = { concurrency: 1, maxUploads: 1 };
  public uploadInput = new EventEmitter<UploadInput>();
  public files: UploadFile[] = [];
  public dragOver: boolean;
  public loadingFile = false;
  public loading = false;
  public ready = false;
  public maxDiffClosingDate = 15;
  public closingDateMin?: Date = new Date();
  public form = new FormGroup({
    closingDate: new FormControl<Date | undefined>(undefined),
    userDealDocumentId: new FormControl<number | undefined>(undefined),
  });
  public descriptionForClosingDate = '';
  public error: string;
  public selected = model<Date | null>(null);

  constructor(
    public sharedService: SharedService,
    public dateService: DateService,
    public promisedPaymentService: PromisedPaymentService,
    public isMoreDaysPipe: IsMoreDaysPipe,
    public dealService: DealService,
    private dialogRef: MatDialogRef<ChangeClosingDateComponent>,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;

    try {
      this.promisedPayment = await firstValueFrom(
        this.promisedPaymentService.getByDealId(this.deal.id),
      ).then((data) => data?.list[0]);
    } catch (e) {
      this.error = this.sharedService.getErrorText(e, true);
    }

    if (this.deal.closingDate) {
      this.form.controls.closingDate.setValue(
        this.dateService.parseAnyDateFormatToDate(this.deal.closingDate),
      );
      // this.updatedClosingDate(true);

      if (!this.promisedPayment) {
        this.closingDateMin = this.dateService.addDaysToDate(
          this.dateService.yyyymmddToDate(this.deal.closingDate),
          1,
        );
      }
    }

    this.loading = false;
    this.ready = true;
  }

  public submit(): void {
    this.updateDealClosingDate();
  }

  public updatedClosingDate(skipUpdate?: boolean): void {
    const closingDate = this.form.controls.closingDate.value;
    if (!closingDate) {
      return;
    }

    if (!skipUpdate) {
      if (this.promisedPayment) {
        this.updatedPromisedPayment(true);
      }
    }
  }

  public async updateDealClosingDate(): Promise<void> {
    if (this.loading || this.loadingFile) return;
    this.error = '';
    const raw = this.form.getRawValue();
    if (!raw.closingDate || !Number.isFinite(raw.closingDate.getTime())) {
      this.error = 'Choose a closing date.';
      return;
    }

    if (
      this.isMoreDaysPipe.transform(
        raw.closingDate,
        this.deal.closingDate,
        this.maxDiffClosingDate,
      ) &&
      !raw.userDealDocumentId
    ) {
      this.error = `In order to update the closing date by more than ${this.maxDiffClosingDate} days, you need to upload the extension / addendum document`;
      return;
    }

    this.loading = true;
    this.dialogRef.disableClose = true;
    try {
      await firstValueFrom(
        this.dealService.updateClosingDate(this.deal.id, {
          closingDate: this.dateService.dateToYyyymmdd(raw.closingDate),
          userDealDocumentId: raw.userDealDocumentId,
        }),
      );

      if (this.dialogRef) {
        this.dialogRef.close(true);
      }
    } catch (e) {
      this.error = this.sharedService.getErrorText(e);
    } finally {
      this.loading = false;
      this.dialogRef.disableClose = false;
    }
  }

  public async updatedPromisedPayment(dry = false): Promise<void> {
    this.loading = true;
    this.error = '';
    const raw = this.form.getRawValue();
    const params: { closingDate?: number } = {};

    if (raw.closingDate) {
      params.closingDate = this.dateService.dateToYyyymmdd(raw.closingDate);
    }

    try {
      this.promisedPaymentPreview = await this.promisedPaymentService
        .update(this.promisedPayment?.id || 0, {
          ...params,
          dryRun: dry,
        })
        .toPromise();

      if (!dry) {
        if (this.dialogRef) {
          this.dialogRef.close(true);
        }
      }
    } catch (e) {
      this.error = this.sharedService.getErrorText(e, true);
    }

    this.loading = false;
  }

  /**
   * Handle uploads.
   */
  public onUploadOutput(
    output: UploadOutput,
    type = UserDealDocumentDocumentTypeEnum.ContractOfSale,
    description = '',
  ): void {
    switch (output.type) {
      case 'allAddedToQueue':
        if (!this.files?.length) {
          return;
        }

        const event: UploadInput = {
          type: 'uploadAll',
          url:
            environment.apiUrl +
            `/api/deal/${
              this.deal.id
            }/document?documentType=${type}&description=${encodeURIComponent(
              description,
            )}`,
          method: 'POST',
          includeWebKitFormBoundary: true,
        };
        this.loadingFile = true;
        this.uploadInput.emit(event);
        break;
      case 'addedToQueue':
        if (typeof output.file !== 'undefined') {
          this.files.push(output.file);
        }
        break;
      case 'uploading':
        if (typeof output.file !== 'undefined') {
          // update current data in files array for uploading file
          const index = this.files.findIndex(
            (file) =>
              typeof output.file !== 'undefined' && file.id === output.file.id,
          );
          this.files[index] = output.file;
        }
        break;
      case 'removed':
        // remove file from array when removed
        this.files = this.files.filter(
          (file: UploadFile) => file !== output.file,
        );
        break;
      case 'dragOver':
        this.dragOver = true;
        break;
      case 'dragOut':
      case 'drop':
        this.dragOver = false;
        break;
      case 'done':
        if (
          output.file &&
          [200, 201].includes(output.file?.responseStatus as number)
        ) {
          if (type === UserDealDocumentDocumentTypeEnum.ExpansionAddendum) {
            this.form.controls.userDealDocumentId.setValue(
              parseInt(output.file.response.id, 10),
            );
          }

          this.error = '';
        } else {
          this.error = output.file?.response.error;
        }

        this.loadingFile = false;
        this.removeFile(output.file?.id || '');
        break;
    }
  }

  public removeFile(id: string): void {
    this.uploadInput.emit({ type: 'remove', id });
  }
}
